import React from 'react';
import { Box, Grid, Typography, Paper, IconButton } from '@mui/material';
import { OpenInFull } from '@mui/icons-material';
import ReactApexChart from 'react-apexcharts';
import { styled } from '@mui/material/styles';
import StatCards from './StatCards';
import ExportButton from './ExportButton';
import BubbleMaps from './BuubleMapChart';

const ChartContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1),
  height: '270px',
  display: 'flex',
  flexDirection: 'column',
  minHeight: '270px'
}));

const ShipmentsTab = ({
  shipments,
  statCards,
  monthlyShipmentCountData,
  monthlyShipmentTypeCountData,
  shipmentCountsByType,
  avgShipmentSizeData,
  poundsMovedMonthlyData,
  topCarriersData,
  topLanesData,
  shipmentsByWeightData,
  weightRangeNames,
  setExpandedChart,
  handleMonthFilter,
  handleCarrierFilter,
  handleWeightFilter,
  handleDonutClick,
  expandedChart
}) => {
  // Prepare export data
  const getShipmentsExportData = () => {
    return shipments.map(shipment => ({
      'Shipment ID': shipment.shipmentId || shipment.id,
      Customer: shipment.destinationCompany,
      Origin: `${shipment.originCity}, ${shipment.originState}`,
      Destination: `${shipment.destinationCity}, ${shipment.destinationState}`,
      Carrier: shipment.carrierName,
      'Shipment Type': shipment.shipmentType,
      'Shipment Mode': shipment.shipmentTypeRule,
      'Pickup Date': shipment.actualPickupDate,
      'Delivery Date': shipment.actualDeliveryDate,
      'Weight (lbs)': shipment.totalGrossWeight,
      Miles: shipment.shipmentMiles
        ? (shipment.shipmentMiles / 1609.344).toFixed(0)
        : 0,
      Status: shipment.shipmentStatus
    }));
  };

  return (
    <Box>
      <Grid container spacing={0.5} sx={{ mb: 1 }}>
        <Grid item xs={12} md={9}>
          <Grid container spacing={0.5}>
            <Grid item xs={12} md={4}>
              <ChartContainer elevation={1}>
                <StatCards cards={statCards} />
              </ChartContainer>
            </Grid>

            {/* Monthly Shipment Count */}
            <Grid item xs={12} md={4}>
              <ChartContainer elevation={1}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1
                  }}
                >
                  <Typography variant='h6' fontWeight='bold' fontSize='1rem'>
                    Monthly Shipment Count
                  </Typography>
                  <IconButton
                    size='small'
                    onClick={() => setExpandedChart('monthlyShipments')}
                  >
                    <OpenInFull fontSize='small' />
                  </IconButton>
                </Box>
                <ReactApexChart
                  options={{
                    chart: {
                      type: 'bar',
                      height: 220,
                      toolbar: { show: false },
                      events: {
                        dataPointSelection: (event, chartContext, config) => {
                          const selectedMonth =
                            monthlyShipmentCountData.categories[
                              config.dataPointIndex
                            ];
                          if (selectedMonth) handleMonthFilter(selectedMonth);
                        }
                      }
                    },
                    plotOptions: {
                      bar: {
                        borderRadius: 2,
                        columnWidth: '50%',
                        dataLabels: { position: 'top' }
                      }
                    },
                    dataLabels: {
                      enabled: true,
                      formatter: val =>
                        val >= 1000 ? (val / 1000).toFixed(1) + 'k' : val,
                      offsetY: -20,
                      style: {
                        fontSize: '9px',
                        colors: ['#304758'],
                        fontWeight: 'bold'
                      }
                    },
                    xaxis: {
                      categories: monthlyShipmentCountData.categories,
                      labels: {
                        style: { fontSize: '10px' },
                        rotate: -45,
                        trim: false
                      }
                    },
                    yaxis: {
                      tickAmount: 6,
                      labels: {
                        formatter: val =>
                          val >= 1000 ? (val / 1000).toFixed(1) + 'k' : val,
                        style: { fontSize: '10px' }
                      }
                    },
                    grid: { padding: { left: 5, right: 5 } },
                    colors: ['#00E396'],
                    tooltip: {
                      y: {
                        formatter: val => val.toLocaleString() + ' shipments'
                      }
                    }
                  }}
                  series={[
                    {
                      name: 'Shipment Count',
                      data: monthlyShipmentCountData.data
                    }
                  ]}
                  type='bar'
                  height={220}
                />
              </ChartContainer>
            </Grid>

            {/* Monthly Shipments by Type */}
            <Grid item xs={12} md={4}>
              <ChartContainer elevation={1}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1
                  }}
                >
                  <Typography variant='h6' fontWeight='bold' fontSize='1rem'>
                    Monthly Shipments by Type
                  </Typography>
                  <IconButton
                    size='small'
                    onClick={() => setExpandedChart('shipmentTypeMonthlyCount')}
                  >
                    <OpenInFull fontSize='small' />
                  </IconButton>
                </Box>
                <ReactApexChart
                  options={{
                    chart: {
                      type: 'bar',
                      height: 220,
                      toolbar: { show: false },
                      events: {
                        dataPointSelection: (event, chartContext, config) => {
                          const selectedMonth =
                            monthlyShipmentTypeCountData.categories[
                              config.dataPointIndex
                            ];
                          if (selectedMonth) handleMonthFilter(selectedMonth);
                        }
                      }
                    },
                    plotOptions: {
                      bar: {
                        borderRadius: 2,
                        columnWidth: '50%',
                        dataLabels: { position: 'top' }
                      }
                    },
                    dataLabels: {
                      enabled: true,
                      formatter: val =>
                        val >= 1000
                          ? (val / 1000).toFixed(1) + 'k'
                          : val > 0
                            ? val
                            : '',
                      offsetY: -20,
                      style: {
                        fontSize: '9px',
                        colors: ['#304758'],
                        fontWeight: 'bold'
                      }
                    },
                    xaxis: {
                      categories: monthlyShipmentTypeCountData.categories,
                      labels: {
                        style: { fontSize: '10px' },
                        rotate: -45,
                        trim: false
                      }
                    },
                    yaxis: {
                      labels: {
                        formatter: val =>
                          val >= 1000
                            ? (val / 1000).toFixed(1) + 'k'
                            : val > 0
                              ? val
                              : '',
                        style: { fontSize: '10px' }
                      }
                    },
                    grid: { padding: { left: 5, right: 5 } },
                    colors: ['#008FFB', '#00E396', '#FF4560'],
                    legend: { position: 'top', fontSize: '12px' },
                    tooltip: {
                      y: {
                        formatter: (val, { seriesIndex }) => {
                          const type = ['LTL', 'Parcel', 'TL'][seriesIndex];
                          return (
                            type + ': ' + val.toLocaleString() + ' shipments'
                          );
                        }
                      }
                    }
                  }}
                  series={monthlyShipmentTypeCountData.series}
                  type='bar'
                  height={220}
                />
              </ChartContainer>
            </Grid>

            {/* Shipments by Type - Donut Chart */}
            <Grid item xs={12} md={4}>
              <ChartContainer elevation={1}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1
                  }}
                >
                  <Typography variant='h6' fontWeight='bold' fontSize='1rem'>
                    # of Shipments by Type
                  </Typography>
                  <IconButton
                    size='small'
                    onClick={() => setExpandedChart('shipmentsByTypeDonut')}
                  >
                    <OpenInFull fontSize='small' />
                  </IconButton>
                </Box>
                <ReactApexChart
                  options={{
                    chart: {
                      type: 'donut',
                      height: 250,
                      events: { dataPointSelection: handleDonutClick }
                    },
                    plotOptions: {
                      pie: {
                        dataLabels: { offset: 20, minAngleToShowLabel: 0 },
                        donut: {
                          size: '65%',
                          labels: {
                            show: true,
                            name: {
                              show: true,
                              fontSize: '12px',
                              fontWeight: 'bold'
                            },
                            value: {
                              show: true,
                              fontSize: '16px',
                              fontWeight: 'bold',
                              formatter: val =>
                                val >= 1000
                                  ? (val / 1000).toFixed(1) + 'k'
                                  : val.toLocaleString()
                            }
                          }
                        }
                      }
                    },
                    dataLabels: {
                      style: {
                        fontSize: '11px',
                        fontWeight: 'bold',
                        colors: ['#1a237e']
                      },
                      background: {
                        enabled: true,
                        foreColor: '#fff',
                        padding: 4,
                        borderRadius: 2,
                        borderWidth: 0,
                        opacity: 0.9
                      },
                      enabled: true,
                      formatter: (_val, opts) =>
                        opts.w.globals.series[
                          opts.seriesIndex
                        ].toLocaleString(),
                      dropShadow: { enabled: false }
                    },
                    legend: {
                      position: 'top',
                      horizontalAlign: 'center',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      markers: { width: 10, height: 10, radius: 5 },
                      itemMargin: { horizontal: 0, vertical: 1 }
                    },
                    colors: [
                      '#008FFB',
                      '#00E396',
                      '#FEB019',
                      '#FF4560',
                      '#775DD0'
                    ],
                    tooltip: {
                      y: {
                        formatter: (val, opts) => {
                          const labels = [
                            'Outbound',
                            'Inbound',
                            'Transfer',
                            'Dropship'
                          ];
                          return `${labels[opts.seriesIndex] || `Type ${opts.seriesIndex + 1}`}: ${val.toLocaleString()} shipments`;
                        }
                      }
                    },
                    labels: ['Outbound', 'Inbound', 'Transfer', 'Dropship']
                  }}
                  series={Object.values(shipmentCountsByType)}
                  type='donut'
                  height={220}
                />
              </ChartContainer>
            </Grid>

            {/* Average Shipment Size */}
            <Grid item xs={12} md={4}>
              <ChartContainer elevation={1}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1
                  }}
                >
                  <Typography variant='h6' fontWeight='bold' fontSize='1rem'>
                    Average Shipment Size (lbs)
                  </Typography>
                  <IconButton
                    size='small'
                    onClick={() => setExpandedChart('avgShipmentSize')}
                  >
                    <OpenInFull fontSize='small' />
                  </IconButton>
                </Box>
                <ReactApexChart
                  options={{
                    chart: {
                      type: 'bar',
                      height: 220,
                      toolbar: { show: false },
                      events: {
                        dataPointSelection: (event, chartContext, config) => {
                          const selectedMonth =
                            avgShipmentSizeData.categories[
                              config.dataPointIndex
                            ];
                          if (selectedMonth) handleMonthFilter(selectedMonth);
                        }
                      }
                    },
                    plotOptions: {
                      bar: {
                        borderRadius: 2,
                        columnWidth: '50%',
                        dataLabels: { position: 'top' }
                      }
                    },
                    dataLabels: {
                      enabled: true,
                      formatter: val =>
                        val >= 1000
                          ? (val / 1000).toFixed(1) + 'k'
                          : Math.round(val),
                      offsetY: -20,
                      style: {
                        fontSize: '9px',
                        colors: ['#304758'],
                        fontWeight: 'bold'
                      }
                    },
                    xaxis: {
                      categories: avgShipmentSizeData.categories,
                      labels: {
                        style: { fontSize: '10px' },
                        rotate: -45,
                        trim: false
                      }
                    },
                    yaxis: {
                      labels: {
                        formatter: val =>
                          val >= 1000
                            ? (val / 1000).toFixed(1) + 'k'
                            : Math.round(val),
                        style: { fontSize: '10px' }
                      }
                    },
                    grid: { padding: { left: 5, right: 5 } },
                    colors: ['#775DD0'],
                    tooltip: {
                      y: {
                        formatter: val =>
                          Math.round(val).toLocaleString() + ' lbs'
                      }
                    }
                  }}
                  series={[
                    {
                      name: 'Avg Shipment Size',
                      data: avgShipmentSizeData.data
                    }
                  ]}
                  type='bar'
                  height={220}
                />
              </ChartContainer>
            </Grid>

            {/* Pounds Moved per Month */}
            <Grid item xs={12} md={4}>
              <ChartContainer elevation={1}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1
                  }}
                >
                  <Typography variant='h6' fontWeight='bold' fontSize='1rem'>
                    Pounds Moved per Month
                  </Typography>
                  <IconButton
                    size='small'
                    onClick={() => setExpandedChart('poundsMovedMonthly')}
                  >
                    <OpenInFull fontSize='small' />
                  </IconButton>
                </Box>
                <ReactApexChart
                  options={{
                    chart: {
                      type: 'bar',
                      height: 220,
                      toolbar: { show: false },
                      events: {
                        dataPointSelection: (event, chartContext, config) => {
                          const selectedMonth =
                            poundsMovedMonthlyData.categories[
                              config.dataPointIndex
                            ];
                          if (selectedMonth) handleMonthFilter(selectedMonth);
                        }
                      }
                    },
                    plotOptions: {
                      bar: {
                        borderRadius: 2,
                        columnWidth: '50%',
                        dataLabels: { position: 'top' }
                      }
                    },
                    dataLabels: {
                      enabled: true,
                      formatter: val => (val / 1000).toFixed(0) + 'k',
                      offsetY: -20,
                      style: {
                        fontSize: '9px',
                        colors: ['#304758'],
                        fontWeight: 'bold'
                      }
                    },
                    xaxis: {
                      categories: poundsMovedMonthlyData.categories,
                      labels: {
                        style: { fontSize: '10px' },
                        rotate: -45,
                        trim: false
                      }
                    },
                    yaxis: {
                      labels: {
                        formatter: val => (val / 1000000).toFixed(1) + 'M',
                        style: { fontSize: '10px' }
                      }
                    },
                    grid: { padding: { left: 5, right: 5 } },
                    colors: ['#FF4560'],
                    tooltip: {
                      y: { formatter: val => val.toLocaleString() + ' lbs' }
                    }
                  }}
                  series={[
                    { name: 'Pounds Moved', data: poundsMovedMonthlyData.data }
                  ]}
                  type='bar'
                  height={220}
                />
              </ChartContainer>
            </Grid>

            {/* Top 5 Carriers */}
            <Grid item xs={12} md={4}>
              <ChartContainer elevation={1}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1
                  }}
                >
                  <Typography variant='h6' fontWeight='bold' fontSize='1rem'>
                    Top 5 Carriers
                  </Typography>
                  <IconButton
                    size='small'
                    onClick={() => setExpandedChart('topCarriers')}
                  >
                    <OpenInFull fontSize='small' />
                  </IconButton>
                </Box>
                <Box sx={{ '& .apexcharts-canvas': { cursor: 'pointer' } }}>
                  <ReactApexChart
                    options={{
                      chart: {
                        type: 'line',
                        height: 250,
                        toolbar: { show: false },
                        events: {
                          dataPointSelection: (event, chartContext, config) => {
                            const selectedCarrier =
                              topCarriersData.carrierNames[
                                config.dataPointIndex
                              ];
                            if (selectedCarrier)
                              handleCarrierFilter(selectedCarrier);
                          }
                        }
                      },
                      stroke: { width: [0, 2] },
                      xaxis: {
                        categories: topCarriersData.carrierNames,
                        labels: {
                          style: { fontSize: '11px', fontWeight: 'bold' },
                          rotate: -45,
                          hideOverlappingLabels: false,
                          trim: false,
                          maxHeight: 60
                        },
                        tickPlacement: 'on'
                      },
                      yaxis: [
                        {
                          seriesName: 'Pounds',
                          labels: {
                            formatter: val => (val / 1000).toFixed(0) + 'K',
                            style: { fontSize: '10px' }
                          }
                        },
                        {
                          seriesName: 'Shipments',
                          opposite: true,
                          labels: {
                            formatter: val => Math.round(val).toLocaleString(),
                            style: { fontSize: '10px' }
                          }
                        }
                      ],
                      colors: ['#008FFB', '#FF4560'],
                      fill: { opacity: [0.85, 1] },
                      grid: { padding: { bottom: 40 } },
                      tooltip: {
                        shared: true,
                        intersect: false,
                        y: {
                          formatter: (val, { seriesIndex, dataPointIndex }) => {
                            const carrierName =
                              topCarriersData.carrierNames[dataPointIndex];
                            if (seriesIndex === 0)
                              return `${carrierName}<br/>Pounds: ${val.toLocaleString()}`;
                            if (seriesIndex === 1)
                              return `${carrierName}<br/>Shipments: ${val.toLocaleString()}`;
                            return val;
                          }
                        }
                      },
                      legend: {
                        position: 'top',
                        horizontalAlign: 'center',
                        fontSize: '12px',
                        itemMargin: { horizontal: 10, vertical: 5 }
                      }
                    }}
                    series={[
                      {
                        name: 'Total Pounds',
                        type: 'column',
                        data: topCarriersData.poundsData
                      },
                      {
                        name: 'Shipments',
                        type: 'line',
                        data: topCarriersData.shipmentsData
                      }
                    ]}
                    height={250}
                  />
                </Box>
              </ChartContainer>
            </Grid>

            {/* Top 5 Lanes */}
            <Grid item xs={12} md={4}>
              <ChartContainer elevation={1}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1
                  }}
                >
                  <Typography variant='h6' fontWeight='bold' fontSize='1rem'>
                    Top 5 Lanes
                  </Typography>
                  <IconButton
                    size='small'
                    onClick={() => setExpandedChart('topLanes')}
                  >
                    <OpenInFull fontSize='small' />
                  </IconButton>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    width: '100%'
                  }}
                >
                  <Box
                    sx={{
                      width: '50%',
                      minWidth: 170,
                      pr: 0.5,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      height: 180
                    }}
                  >
                    {topLanesData.displayNames.map((lane, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          height: 30
                        }}
                      >
                        <Typography
                          variant='caption'
                          sx={{
                            fontSize: '11px',
                            fontWeight: 'bold',
                            color: '#304758'
                          }}
                        >
                          {index + 1}. {lane}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                  <Box
                    sx={{
                      width: '50%',
                      position: 'relative',
                      marginLeft: '-10px'
                    }}
                  >
                    <ReactApexChart
                      options={{
                        chart: {
                          type: 'bar',
                          height: 220,
                          toolbar: { show: false },
                          offsetY: 1
                        },
                        plotOptions: {
                          bar: {
                            horizontal: true,
                            barHeight: '40%',
                            borderRadius: 2,
                            dataLabels: { position: 'right' }
                          }
                        },
                        dataLabels: {
                          enabled: true,
                          formatter: val =>
                            '\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0' +
                            val.toLocaleString(),
                          style: {
                            fontSize: '11px',
                            colors: ['#304758'],
                            fontWeight: 'bold'
                          }
                        },
                        xaxis: {
                          labels: {
                            formatter: val => '\u00A0' + val.toLocaleString(),
                            style: { fontSize: '10px' }
                          }
                        },
                        yaxis: {
                          show: false,
                          labels: { show: false },
                          axisBorder: { show: false },
                          axisTicks: { show: false }
                        },
                        colors: ['#FEB019'],
                        tooltip: {
                          y: {
                            formatter: (val, { dataPointIndex }) => {
                              const lane = topLanesData.details[dataPointIndex];
                              return `${lane.originCity} → ${lane.destinationCity}<br/>Shipments: ${val.toLocaleString()}`;
                            }
                          }
                        },
                        grid: {
                          padding: { left: 0, right: 30, top: -20, bottom: 20 },
                          yaxis: { lines: { show: false } }
                        }
                      }}
                      series={[
                        { name: 'Shipments', data: topLanesData.shipmentCounts }
                      ]}
                      type='bar'
                      height={220}
                    />
                  </Box>
                </Box>
              </ChartContainer>
            </Grid>

            {/* Shipments by Weight */}
            <Grid item xs={12} md={4}>
              <ChartContainer elevation={1}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1
                  }}
                >
                  <Typography variant='h6' fontWeight='bold' fontSize='1rem'>
                    Shipments by Weight (lbs)
                  </Typography>
                  <IconButton
                    size='small'
                    onClick={() => setExpandedChart('shipmentsByWeight')}
                  >
                    <OpenInFull fontSize='small' />
                  </IconButton>
                </Box>
                <Box sx={{ overflowX: 'auto', overflowY: 'visible' }}>
                  <ReactApexChart
                    options={{
                      chart: {
                        type: 'bar',
                        height: 280,
                        toolbar: { show: false },
                        events: {
                          dataPointSelection: (event, chartContext, config) => {
                            const selectedWeightRange =
                              weightRangeNames[config.dataPointIndex];
                            if (selectedWeightRange)
                              handleWeightFilter(selectedWeightRange);
                          }
                        }
                      },
                      plotOptions: {
                        bar: {
                          distributed: true,
                          horizontal: true,
                          barHeight: '60%',
                          dataLabels: {
                            position: 'right',
                            hideOverflowingLabels: false
                          }
                        }
                      },
                      dataLabels: {
                        enabled: true,
                        formatter: val => {
                          const pad =
                            '\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0';
                          return pad + val.toLocaleString();
                        },
                        style: {
                          fontSize: '11px',
                          fontWeight: 'bold',
                          colors: ['#304758'],
                          textShadow: '1px 1px 2px white'
                        },
                        background: { enabled: false },
                        offsetX: 20
                      },
                      xaxis: {
                        labels: {
                          formatter: val => val.toLocaleString(),
                          style: { fontSize: '11px' }
                        }
                      },
                      yaxis: {
                        categories: [
                          '<500',
                          '500-999',
                          '1,000-1,999',
                          '2,000-4,999',
                          '5,000-9,999',
                          '10,000-19,999',
                          '20,000-40,000',
                          '>40,000'
                        ],
                        labels: {
                          show: true,
                          minWidth: 80,
                          maxWidth: 100,
                          style: { fontSize: '11px', fontWeight: 'bold' }
                        }
                      },
                      legend: { show: false },
                      grid: { padding: { left: 5, right: 5 } },
                      colors: [
                        '#008FFB',
                        '#00E396',
                        '#FEB019',
                        '#FF4560',
                        '#775DD0',
                        '#3F51B5',
                        '#546E7A',
                        '#D4526E'
                      ],
                      tooltip: {
                        y: {
                          formatter: (val, { dataPointIndex }) => {
                            const weightRanges = [
                              'Under 500 lbs',
                              '500-749 lbs',
                              '750-999 lbs',
                              '1,000-1,999 lbs',
                              '2,000-4,999 lbs',
                              '5,000-9,999 lbs',
                              '10,000-19,999 lbs',
                              '20,000-40,000 lbs',
                              'Over 40,000 lbs'
                            ];
                            return `${weightRanges[dataPointIndex]}: ${val.toLocaleString()} shipments`;
                          }
                        }
                      },
                      labels: [
                        '<500',
                        '500-999',
                        '1,000-1,999',
                        '2,000-4,999',
                        '5,000-9,999',
                        '10,000-19,999',
                        '20,000-40,000',
                        '>40,000'
                      ]
                    }}
                    series={[
                      { name: 'Shipments', data: shipmentsByWeightData }
                    ]}
                    type='bar'
                    height={280}
                  />
                </Box>
              </ChartContainer>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} md={3}>
          <BubbleMaps
            shipments={shipments}
            setExpandedChart={setExpandedChart}
            expandedChart={expandedChart}
            tabValue={1}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ShipmentsTab;
